import type { ReactNode } from 'react';

import { cn } from '../lib/cn.js';

export interface PublicShellProps {
  /** Marca da plataforma. Renderizada no topo. */
  brand?: ReactNode;
  /** Conteúdo principal. */
  children: ReactNode;
  /** Rodapé opcional (ex: aviso LGPD, contato). */
  footer?: ReactNode;
  /** Classe extra no container central. */
  contentClassName?: string;
}

/**
 * Shell para rotas públicas e sem navegação (ex: `/onboarding/[token]`,
 * páginas legais, erros 404/500). Mobile-first, respeita safe-area e suporta
 * light/dark como os demais shells.
 *
 * @example
 * <PublicShell brand={<Logo />} footer={<LGPDNotice />}>
 *   <OnboardingForm token={token} />
 * </PublicShell>
 */
export function PublicShell({ brand, children, footer, contentClassName }: PublicShellProps) {
  return (
    <div
      className={cn(
        'flex min-h-dvh flex-col bg-background text-foreground',
        'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {brand ? (
        <header className="flex h-16 items-center border-b border-border px-4 sm:px-6 lg:px-8">
          {brand}
        </header>
      ) : null}
      <main
        className={cn(
          'mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-6 sm:px-6 sm:py-8 lg:px-8',
          contentClassName,
        )}
      >
        {children}
      </main>
      {footer ? (
        <footer className="border-t border-border px-4 py-4 text-center text-sm text-muted-foreground sm:px-6 lg:px-8">
          {footer}
        </footer>
      ) : null}
    </div>
  );
}
